import { createStore } from '@stencil/store';
import jwt_decode from 'jwt-decode';

import { STORAGE_KEYS } from '../../constants/constants';
import { ISessionStoreState } from '../../models/sessionStoreState';
import store from '../../store/store';
import { AccountAndTenantsInstanceResolver } from '../accountAndTenants/accountAndTenantsInstanceResolver';
import { AppInsightsInstanceResolver } from '../appInsights/appInsightsInstanceResolver';
import { isStandalone } from '../EnvironmentUtil';
import GlobalEventEmitter from '../events/globalEventEmitters';
import { Features } from '../featureFlags/featureFlagConstants';
import { getFeatureFlag } from '../featureFlags/featureFlags';
import {
    isFalsyOrWhitespace,
    sanitizePii,
    sanitizeToken,
} from '../helper';
import { setPortalShellUserLanguage } from '../languageHelper';
import Logger from '../logger/logger';
import { setPortalShellUserTheme } from '../themeHelper';

const currentGravatarStorageKey = STORAGE_KEYS.PORTAL_GRAVATAR;

export const sessionStore = createStore({
    isSignInInitialized: false,
    isSignInSuccessful: false,
    accessToken: '',
    selfAuthAccessToken: '',
    fullName: '',
    userId: '',
    email: '',
    avatarUrl: localStorage.getItem(currentGravatarStorageKey) ?? '',
    organizationId: '',
    isHost: false,
    connectionType: '',
} as ISessionStoreState);

sessionStore.on('set', (key, newValue, oldValue) => {
    if (key === 'accessToken' || key === 'selfAuthAccessToken') {
        Logger.debug('store', `key: ${key}\noldValue: ${sanitizeToken(oldValue)} \nnewValue: ${sanitizeToken(newValue)} \n`, { state: store.state });
        return;
    }

    if (key === 'email' || key === 'fullName') {
        Logger.debug('store', `key: ${key}\noldValue: ${sanitizePii(oldValue)} \nnewValue: ${sanitizePii(newValue)} \n`, { state: store.state });
        return;
    }

    // Used to check hydration
    Logger.debug('store', `key: ${key}\noldValue: ${JSON.stringify(oldValue)} \nnewValue: ${JSON.stringify(newValue)} \n`, { state: store.state });
});

sessionStore.onChange('accessToken', async (accessToken) => {
    if (!getFeatureFlag(Features.EnablePortalShellSelfAuth.name) || !sessionStore.get('selfAuthAccessToken')) {
        await handleAccessTokenChange(accessToken);
    }
});
sessionStore.onChange('selfAuthAccessToken', async (accessToken) => {
    await handleAccessTokenChange(accessToken, { isSelfAuth: true });
});

async function handleAccessTokenChange(accessToken: string, { isSelfAuth = false } = {}) {
    if (isFalsyOrWhitespace(accessToken)) {
        return;
    }

    const success = decodeAccessToken(accessToken);
    if (!success) {
        return;
    }

    const prevUserId = AppInsightsInstanceResolver.ShellInstance.getPlatformInfoField('cloudUserId') ??
        AppInsightsInstanceResolver.ShellInstance.getPlatformInfoField('localUserId');
    const newUserId = sessionStore.get('userId');

    /**
    * This will help us debugging user issues via app insights easier (ask them to give us this information from the console)
    *
    * TODO: Make it even a better experience (like a button to copy to clipboard)
    */
    if (prevUserId !== newUserId) {
        Logger.info(
            [ 'AppInsights Debug', 'Access Token Changed' ],
            `New User Id / UserId: ${newUserId}`,
            { forcePrintToConsole: true },
        );
    }

    // Set partial data of app insights based off access token info
    AppInsightsInstanceResolver.SetPlatformInfo({
        cloudUserId: newUserId,
        localUserId: newUserId,
        cloudOrganizationId: sessionStore.get('organizationId'),
        isHost: sessionStore.get('isHost'),
    });

    const tokenType = isSelfAuth ? 'session self auth' : 'passed-in';
    Logger.debug([ 'api', 'token' ], `using ${tokenType} access token`, { state: store.state });

    const effectiveToken = getAccessToken(accessToken);
    Logger.debug([ 'account', 'api', 'language', 'featureFlags' ], 'api call, user language, and feature flags calls');
    await Promise.all([
        AccountAndTenantsInstanceResolver.Instance?.fetchAccountAndTenants(false, effectiveToken, sessionStore.get('isHost')),
        window.PortalShell.FeatureFlags.fetchFeatureFlags(sessionStore, effectiveToken),
        ...(!isStandalone() ? [ setPortalShellUserLanguage(store, effectiveToken) ] : []),
        ...(!isStandalone() ? [ setPortalShellUserTheme(store, effectiveToken) ] : []),
        ...(getFeatureFlag(Features.EnableNotification.name) ? [ window.PortalShell.SignalRService.updateConnection() ] : []),
    ]);
}

function decodeAccessToken(accessToken: string) {
    let decodedToken: any = null;
    try {
        decodedToken = jwt_decode(accessToken);
    } catch (e) {
        Logger.error([ 'store', 'token' ], 'access token was invalid, Details : ' + e.toString());
    }

    if (decodedToken == null) {
        return false;
    }

    if (!decodedToken.scope) {
        Logger.warn('token', `Warning: The ID Token should not be passed. Please pass an access token to the token prop.`, { forcePrintToConsole: true });
    }

    const hasName = decodedToken.first_name || decodedToken.last_name;
    const fullName = hasName ? `${decodedToken.first_name} ${decodedToken.last_name}` : decodedToken.name;

    sessionStore.set('fullName', fullName);
    sessionStore.set('email', decodedToken.email);
    sessionStore.set('avatarUrl', decodedToken.picture);
    sessionStore.set('organizationId', decodedToken.prt_id);
    sessionStore.set('isHost', decodedToken.host?.toLowerCase() === 'true');
    sessionStore.set('userId', decodedToken.sub);
    sessionStore.set('connectionType', decodedToken.ext_idp_disp_name?.toLowerCase() ?? '');

    GlobalEventEmitter.emitAccessTokenChanged({
        accessToken,
        organizationId: decodedToken.prt_id,
    });

    if (decodedToken.picture) {
        localStorage.setItem(currentGravatarStorageKey, decodedToken.picture);
    }

    return true;
}

// Internal use only
export function getAccessToken(defaultToken?: string): string {
    Logger.debug('session', `getAccessToken called with sanitized defaultToken: ${sanitizeToken(defaultToken)} and sanitized accessToken: ${sanitizeToken(sessionStore.get('accessToken'))}`, { state: sessionStore.state });
    const fallbackToken = !isFalsyOrWhitespace(defaultToken) ? defaultToken : sessionStore.get('accessToken');
    const selfAuthFlag = getFeatureFlag(Features.EnablePortalShellSelfAuth.name);
    if (!selfAuthFlag) {
        Logger.info('session', `getAccessToken returning fallback access token: flag=${selfAuthFlag}, signInInitialized=${sessionStore.get('isSignInInitialized')}, defaultToken=${sanitizeToken(defaultToken)}, storeToken=${sanitizeToken(sessionStore.get('accessToken'))}, selfAuthToken=${sanitizeToken(sessionStore.get('selfAuthAccessToken'))}`, { state: sessionStore.state });
        return fallbackToken ?? '';
    }
    const selfAuthAccessToken = sessionStore.get('selfAuthAccessToken');
    Logger.debug('session', `returning self auth access token, flag=${selfAuthFlag} selfAuthAccessToken=${sanitizeToken(selfAuthAccessToken)}`, { state: sessionStore.state });
    return selfAuthAccessToken;
}
